// src/services/flipbookService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  getDocs, 
  query, 
  orderBy,
  where,
  writeBatch,
  increment,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';

// Use same worker setup as pdfService
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

class FlipbookService {
  constructor() {
    this.booksCollection = 'pb-books';
    this.bookletsSubcollection = 'pb-booklets';
    this.pagesSubcollection = 'pages';
  }

  // ==================== BOOKLET METHODS ====================

  async getBooklets(bookId) {
    try {
      const bookletsRef = collection(db, this.booksCollection, bookId, this.bookletsSubcollection);
      const q = query(bookletsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting booklets:', error);
      throw error;
    }
  }

  async getBooklet(bookId, bookletId) {
    try {
      const bookletRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId);
      const docSnap = await getDoc(bookletRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      throw new Error('Booklet not found');
    } catch (error) {
      console.error('Error getting booklet:', error);
      throw error;
    }
  }

  async createBooklet(bookId, bookletData) {
    try {
      const bookletsRef = collection(db, this.booksCollection, bookId, this.bookletsSubcollection);
      
      const existingBooklets = await this.getBooklets(bookId);
      
      const docRef = await addDoc(bookletsRef, {
        title: bookletData.title || `Booklet ${existingBooklets.length + 1}`,
        description: bookletData.description || '',
        order: bookletData.order ?? existingBooklets.length,
        pageCount: 0,
        pageWidth: bookletData.pageWidth || 800,
        pageHeight: bookletData.pageHeight || 1000,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await this.updateBookStats(bookId, { 
        totalBooklets: increment(1) 
      });

      return { id: docRef.id, ...bookletData };
    } catch (error) {
      console.error('Error creating booklet:', error);
      throw error;
    }
  }

  async updateBooklet(bookId, bookletId, updates) {
    try {
      const bookletRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId);
      await updateDoc(bookletRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating booklet:', error);
      throw error;
    }
  }

  async deleteBooklet(bookId, bookletId) {
    try {
      const pages = await this.getPages(bookId, bookletId);
      const batch = writeBatch(db);

      pages.forEach(page => {
        const pageRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection, page.id);
        batch.delete(pageRef);
      });

      await batch.commit();

      const bookletRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId);
      await deleteDoc(bookletRef);

      await this.updateBookStats(bookId, { 
        totalBooklets: increment(-1),
        totalPages: increment(-pages.length)
      });

      return { success: true };
    } catch (error) {
      console.error('Error deleting booklet:', error);
      throw error;
    }
  }

  async reorderBooklets(bookId, bookletIds) {
    try {
      const batch = writeBatch(db);

      bookletIds.forEach((bookletId, index) => {
        const bookletRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId);
        batch.update(bookletRef, { order: index, updatedAt: serverTimestamp() });
      });

      await batch.commit();
      return { success: true };
    } catch (error) {
      console.error('Error reordering booklets:', error);
      throw error;
    }
  }

  // ==================== PAGE METHODS ====================

  async getPages(bookId, bookletId) {
    try {
      const pagesRef = collection(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection);
      const q = query(pagesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pages:', error);
      throw error;
    }
  }

  async getPage(bookId, bookletId, pageId) {
    try {
      const pageRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection, pageId);
      const docSnap = await getDoc(pageRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      throw new Error('Page not found');
    } catch (error) {
      console.error('Error getting page:', error);
      throw error;
    }
  }

  async createPage(bookId, bookletId, pageData) {
    try {
      const pagesRef = collection(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection);
      
      const docRef = await addDoc(pagesRef, {
        imageUrl: pageData.imageUrl,
        order: pageData.order,
        pageNumber: pageData.pageNumber,
        voiceUrl: pageData.voiceUrl || null,
        voiceMetadata: pageData.voiceMetadata || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await this.updateBooklet(bookId, bookletId, {
        pageCount: increment(1)
      });

      await this.updateBookStats(bookId, {
        totalPages: increment(1)
      });

      return { id: docRef.id, ...pageData };
    } catch (error) {
      console.error('Error creating page:', error);
      throw error;
    }
  }

  async updatePage(bookId, bookletId, pageId, updates) {
    try {
      const pageRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection, pageId);
      await updateDoc(pageRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating page:', error);
      throw error;
    }
  }

  async deletePage(bookId, bookletId, pageId) {
    try {
      const pageRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection, pageId);
      await deleteDoc(pageRef);

      await this.updateBooklet(bookId, bookletId, {
        pageCount: increment(-1)
      });

      await this.updateBookStats(bookId, {
        totalPages: increment(-1)
      });

      return { success: true };
    } catch (error) {
      console.error('Error deleting page:', error);
      throw error;
    }
  }

  async reorderPages(bookId, bookletId, pageIds) {
    try {
      const batch = writeBatch(db);

      pageIds.forEach((pageId, index) => {
        const pageRef = doc(db, this.booksCollection, bookId, this.bookletsSubcollection, bookletId, this.pagesSubcollection, pageId);
        batch.update(pageRef, { order: index, updatedAt: serverTimestamp() });
      });

      await batch.commit();
      return { success: true };
    } catch (error) {
      console.error('Error reordering pages:', error);
      throw error;
    }
  }

  // ==================== PDF UPLOAD METHODS ====================

  async uploadPdfToBooklet(bookId, bookletId, pdfFile, onProgress = null) {
    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      
      const loadingTask = getDocument({
        data: pdfBytes,
        useWorkerFetch: false,
        isEvalSupported: false,
        useSystemFonts: true,
        disableAutoFetch: true,
        disableStream: true
      });
      
      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;

      // Get dimensions from first page
      const firstPage = await pdf.getPage(1);
      const viewport = firstPage.getViewport({ scale: 1.0 });
      
      await this.updateBooklet(bookId, bookletId, {
        pageWidth: Math.round(viewport.width),
        pageHeight: Math.round(viewport.height),
        totalPages
      });

      const uploadedPages = [];

      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        try {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 2.0 });
          
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          const context = canvas.getContext('2d');
          
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
            background: 'white',
            enableWebGL: false
          };
          
          await page.render(renderContext).promise;
          
          // Convert canvas to blob
          const blob = await new Promise(resolve => 
            canvas.toBlob(resolve, 'image/jpeg', 0.95)
          );
          
          // Upload to storage
          const imagePath = `pb-books/${bookId}/booklets/${bookletId}/pages/page-${pageNumber}.jpg`;
          const imageUrl = await this.uploadPageImage(blob, imagePath);

          // Create page document
          const pageData = await this.createPage(bookId, bookletId, {
            imageUrl,
            order: pageNumber - 1,
            pageNumber: pageNumber
          });

          uploadedPages.push(pageData);

          // Clean up
          canvas.remove();

          // Progress callback
          if (onProgress) {
            onProgress({
              current: pageNumber,
              total: totalPages,
              percentage: Math.round((pageNumber / totalPages) * 100)
            });
          }
        } catch (pageError) {
          console.error(`Error processing page ${pageNumber}:`, pageError);
        }
      }

      await pdf.destroy();

      return {
        success: true,
        totalPages,
        pages: uploadedPages
      };
    } catch (error) {
      console.error('Error uploading PDF:', error);
      throw error;
    }
  }

  // ==================== FILE UPLOAD METHODS ====================

  async uploadPageImage(imageBlob, path) {
    try {
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, imageBlob);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('Error uploading page image:', error);
      throw error;
    }
  }

  async uploadPageVoice(bookId, bookletId, pageId, audioBlob) {
    try {
      const path = `pb-books/${bookId}/booklets/${bookletId}/pages/${pageId}/narration.webm`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, audioBlob);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('Error uploading page voice:', error);
      throw error;
    }
  }

  async deletePageVoice(bookId, bookletId, pageId) {
    try {
      const path = `pb-books/${bookId}/booklets/${bookletId}/pages/${pageId}/narration.webm`;
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting page voice:', error);
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
      return { success: true };
    }
  }

  async setPageVoice(bookId, bookletId, pageId, voiceData) {
    try {
      let voiceUrl = null;
      let voiceMetadata = null;

      if (voiceData && voiceData.audioBlob) {
        voiceUrl = await this.uploadPageVoice(bookId, bookletId, pageId, voiceData.audioBlob);
        voiceMetadata = {
          duration: voiceData.duration || 0,
          uploadedAt: new Date().toISOString()
        };
      } else {
        await this.deletePageVoice(bookId, bookletId, pageId);
      }

      await this.updatePage(bookId, bookletId, pageId, {
        voiceUrl,
        voiceMetadata
      });

      return { voiceUrl, voiceMetadata };
    } catch (error) {
      console.error('Error setting page voice:', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  async updateBookStats(bookId, updates) {
    try {
      const bookRef = doc(db, this.booksCollection, bookId);
      await updateDoc(bookRef, {
        ...Object.keys(updates).reduce((acc, key) => {
          acc[`flipbookStats.${key}`] = updates[key];
          return acc;
        }, {}),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating book stats:', error);
      throw error;
    }
  }

  async getFlipbookStats(bookId) {
    try {
      const booklets = await this.getBooklets(bookId);
      
      let totalPages = 0;
      for (const booklet of booklets) {
        const pages = await this.getPages(bookId, booklet.id);
        totalPages += pages.length;
      }

      return {
        totalBooklets: booklets.length,
        totalPages,
        booklets: booklets.map(b => ({
          id: b.id,
          title: b.title,
          pageCount: b.pageCount || 0
        }))
      };
    } catch (error) {
      console.error('Error getting flipbook stats:', error);
      return {
        totalBooklets: 0,
        totalPages: 0,
        booklets: []
      };
    }
  }

  // ==================== ANALYTICS METHODS ====================

  async trackPageView(bookId, bookletId, pageNumber, userId = null) {
    try {
      const analyticsRef = collection(db, this.booksCollection, bookId, 'analytics');
      await addDoc(analyticsRef, {
        type: 'page_view',
        bookletId,
        pageNumber,
        userId: userId || 'anonymous',
        timestamp: serverTimestamp(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server'
      });

      return { success: true };
    } catch (error) {
      console.error('Error tracking page view:', error);
      return { success: false };
    }
  }

  async trackFlipInteraction(bookId, bookletId, fromPage, toPage, userId = null) {
    try {
      const analyticsRef = collection(db, this.booksCollection, bookId, 'analytics');
      await addDoc(analyticsRef, {
        type: 'page_flip',
        bookletId,
        fromPage,
        toPage,
        userId: userId || 'anonymous',
        timestamp: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error tracking flip interaction:', error);
      return { success: false };
    }
  }

  // ==================== RESPONSE METHODS ====================

  async createPageResponse(bookId, bookletId, responseData) {
    try {
      const responsesRef = collection(db, this.booksCollection, bookId, 'pb-responses');
      const docRef = await addDoc(responsesRef, {
        ...responseData,
        bookId,
        bookletId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { id: docRef.id, ...responseData };
    } catch (error) {
      console.error('Error creating page response:', error);
      throw error;
    }
  }

  async getPageResponses(bookId, bookletId, pageNumber) {
    try {
      const responsesRef = collection(db, this.booksCollection, bookId, 'pb-responses');
      const q = query(
        responsesRef, 
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(response => 
          response.bookletId === bookletId && 
          response.pageNumber === pageNumber
        );
    } catch (error) {
      console.error('Error getting page responses:', error);
      return [];
    }
  }

  async getAllBookResponses(bookId) {
    try {
      const responsesRef = collection(db, this.booksCollection, bookId, 'pb-responses');
      const q = query(responsesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all book responses:', error);
      return [];
    }
  }

  async updatePageResponse(bookId, responseId, updates) {
    try {
      const responseRef = doc(db, this.booksCollection, bookId, 'pb-responses', responseId);
      await updateDoc(responseRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating page response:', error);
      throw error;
    }
  }

  async deletePageResponse(bookId, responseId) {
    try {
      const responseRef = doc(db, this.booksCollection, bookId, 'pb-responses', responseId);
      await deleteDoc(responseRef);

      return { success: true };
    } catch (error) {
      console.error('Error deleting page response:', error);
      throw error;
    }
  }

  async uploadResponseFile(bookId, bookletId, pageNumber, file) {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storagePath = `pb-${bookId}/responses/${bookletId}/page-${pageNumber}/${fileName}`;
      const storageRef = ref(storage, storagePath);
      
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      return {
        url,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        storagePath
      };
    } catch (error) {
      console.error('Error uploading response file:', error);
      throw error;
    }
  }

  async createPageQuestion(bookId, bookletId, questionData) {
    try {
      const questionsRef = collection(db, 'pb-questions');
      const docRef = await addDoc(questionsRef, {
        ...questionData,
        bookId,
        bookletId,
        createdAt: new Date().toISOString()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating question:', error);
      throw error;
    }
  }

  async getPageQuestions(bookId, bookletId, pageNumber) {
    try {
      const questionsRef = collection(db, 'pb-questions');
      const q = query(
        questionsRef,
        where('bookId', '==', bookId),
        where('bookletId', '==', bookletId),
        where('pageNumber', '==', pageNumber)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting questions:', error);
      throw error;
    }
  }

  async deletePageQuestion(questionId) {
    try {
      await deleteDoc(doc(db, 'pb-questions', questionId));
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  }

  async updatePageQuestion(questionId, updates) {
    try {
      const questionRef = doc(db, 'pb-questions', questionId);
      await updateDoc(questionRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  }
}

export const flipbookService = new FlipbookService();